import { useRef, useState } from "react";
import type { ExternalFileReference } from "../model/inp/externalFiles.ts";
import { pluralText, useT } from "../i18n/hooks.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";
import { FolderOpenIcon } from "./FolderOpenIcon.tsx";

/**
 * Shown only when running a simulation finds at least one
 * `[TIMESERIES]`/direct-`[RAINGAGES]` FILE reference the project doesn't
 * resolve yet — gated at run time, not project-open time, since a project
 * opened only to inspect or edit the network never needs this data.
 *
 * One `<input type="file">` per missing reference, not a single `multiple`
 * picker with after-the-fact name matching: each row's picked file is
 * stored under that row's own expected basename regardless of the local
 * file's actual name.
 *
 * `onRun` always proceeds with whatever's currently supplied, even if some
 * rows are still missing — `simulateProject.ts`'s existing warning path
 * (zero rainfall for that source) already handles that gracefully.
 *
 * `missing` is captured once, via `useState`'s lazy initializer, not
 * re-read on every prop update — otherwise a just-supplied row would
 * vanish from this list immediately, before the user sees it register as
 * "Provided". `supplied` (read fresh every render) still drives each row's
 * live status.
 */
export function ExternalFilesDialog({
  missing,
  supplied,
  onSupply,
  onRun,
  onClose,
}: {
  missing: ExternalFileReference[];
  /** Drives each row's "Provided" status live, independently of the frozen `rows` list below. */
  supplied: ReadonlyMap<string, string>;
  onSupply: (basename: string, fileName: string, content: string) => void;
  onRun: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [rows] = useState(missing);
  return (
    <Dialog title={t("externalFiles.title")} onClose={onClose}>
      <p className="external-files-hint">
        {pluralText(t, rows.length, "externalFiles.hintOne", "externalFiles.hint")}
      </p>
      <ul className="external-files-list">
        {rows.map((ref) => (
          <ExternalFileRow
            key={`${ref.section}:${ref.name}:${ref.basename}`}
            reference={ref}
            alreadyProvided={supplied.has(ref.path) || supplied.has(ref.basename)}
            onSupply={(fileName, content) => onSupply(ref.basename, fileName, content)}
          />
        ))}
      </ul>
      <DialogActions
        primary={{ label: t("externalFiles.runButton"), onClick: onRun }}
        onClose={onClose}
      />
    </Dialog>
  );
}

function ExternalFileRow({
  reference,
  alreadyProvided,
  onSupply,
}: {
  reference: ExternalFileReference;
  /** True when `supplied` already resolves this reference, before any pick made in this dialog instance. */
  alreadyProvided: boolean;
  onSupply: (fileName: string, content: string) => void;
}) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickedName, setPickedName] = useState<string | null>(null);

  async function handleChange(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    const content = await file.text();
    setPickedName(file.name);
    onSupply(file.name, content);
  }

  const shownName = pickedName ?? (alreadyProvided ? reference.basename : null);

  return (
    <li className="external-files-row">
      <span className="external-files-row-label">
        {t("externalFiles.rowLabel", {
          section: `[${reference.section}]`,
          name: reference.name,
          filename: reference.basename,
        })}
      </span>
      {shownName ? (
        <span className="external-files-row-provided">
          {t("externalFiles.provided", { fileName: shownName })}
        </span>
      ) : null}
      <button type="button" className="button" onClick={() => inputRef.current?.click()}>
        <FolderOpenIcon />
        {shownName ? t("externalFiles.replace") : t("externalFiles.chooseFile")}
      </button>
      <input
        ref={inputRef}
        type="file"
        className="landing-file-input"
        onChange={(e) => void handleChange(e.target.files)}
      />
    </li>
  );
}
